import { Link } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Folder01Icon, PlusSignIcon } from '@hugeicons/core-free-icons';
import type { ProjectWithPreview } from '../../lib/api/projects';
import { ProjectCard } from '../../pages/projects/ProjectCard';
import { ProjectCardSkeleton } from '../../pages/projects/ProjectCardSkeleton';
import { log } from '@/lib/logger';
import { useExtensionDetection } from '../../hooks/useExtensionDetection';
import { readPendingExtensionOpen } from '@/lib/pendingExtensionOpen';
import { EmptyState } from '../ui/EmptyState';

/** How many cards the section shows: the newest by updatedAt (DashboardPage slices). */
export const RECENT_PROJECTS_LIMIT = 4;

interface RecentProjectsSectionProps {
  /**
   * The same list payload the Projects page renders (useProjects with
   * includePreviews + includeCounts), already sorted newest-first and sliced.
   * Same data, same ProjectCard: the two surfaces cannot drift.
   */
  items: ProjectWithPreview[];
  /** New dashboards need one next step before they have issue data. */
  gettingStarted?: boolean;
}

/** Same grid as the Projects page at this width (ProjectsPage.tsx grid). */
const GRID = 'grid grid-cols-1 sm:grid-cols-2 gap-5';

export function RecentProjectsSection({ items, gettingStarted = false }: RecentProjectsSectionProps) {
  const { detected: extensionDetected } = useExtensionDetection();
  log.info('[RecentProjectsSection] Rendering, projects:', { length: items.length });

  const pendingDomain = (() => {
    const pending = readPendingExtensionOpen();
    if (!pending) return null;
    try {
      return new URL(pending.url).hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  })();

  if (gettingStarted) {
    const project = items.find(item => !item.isDemo);
    const sample = items.find(item => item.isDemo);
    const preview = project ?? sample;
    return <section aria-label="Getting started" className={`grid grid-cols-1 gap-5 ${preview ? 'lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : ''}`}>
      <div className="card-sm flex items-center justify-center">
        <EmptyState icon={Folder01Icon}
          title={project ? 'Capture your first issue' : 'Create your first project'}
          hint={project ? 'Open your project to review a page and capture an issue. Your overview and activity will appear here.' : 'Create a project to keep your site’s issues, reviews and progress together.'}
          action={<div className="flex flex-wrap items-center justify-center gap-3">
            <Link className="btn-primary" to={project ? `/projects/${project.id}/issues` : '/projects?new=1'}><span className="text-cap-trim">{project ? 'Open project' : 'Create project'}</span></Link>
            {sample && <Link className="btn-ghost text-primary-600" to={`/projects/${sample.id}/issues`}><span className="text-cap-trim">Explore sample project</span></Link>}
          </div>}
        />
      </div>
      {preview && <ProjectCard project={preview} />}
    </section>;
  }

  return (
    <section id="recent-projects" aria-label="Recent projects" className="flex flex-col">
      <h2 className="section-label mb-3 flex items-center gap-2">
        <HugeiconsIcon icon={Folder01Icon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-400" aria-hidden="true" />
        <span className="flex-1">My Projects</span>
      </h2>
      <div className="flex-1">
        <div className={GRID}>
          {items.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              thumbnail={
                pendingDomain && project.primaryDomain === pendingDomain
                  ? <PendingOpenThumbnail extensionDetected={extensionDetected} />
                  : undefined
              }
            />
          ))}
          {items.length <= 1 && <AddProjectCard />}
        </div>
      </div>
    </section>
  );
}

/**
 * The grid's empty cell as a call to action: with 0 or 1 cards the row has a
 * blank slot, and a dashed card there invites the next project (Emily,
 * 2026-10-10). A second card fills the slot, so the CTA leaves. Same create
 * route as the Projects page's New project button (ProjectsPage reads ?new=1).
 */
function AddProjectCard() {
  return (
    <Link to="/projects?new=1" className="card-sm card-interactive border-dashed flex">
      <EmptyState
        size="md"
        icon={PlusSignIcon}
        title="Add project"
        hint="Keep each site’s issues, reviews and progress together."
        className="flex-1 py-8"
      />
    </Link>
  );
}

/**
 * The thumbnail of the site the user chose in onboarding while its auto-open
 * is pending. Spins only when the extension is actually present (the auto-open
 * is firing); otherwise a spinner is a false-progress signal, since nothing
 * happens server-side, so it states the real next action instead (the Install
 * button lives in the ExtensionInstallToast above this section).
 */
function PendingOpenThumbnail({ extensionDetected }: { extensionDetected: boolean }) {
  return extensionDetected ? (
    <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gray-50">
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary-400 border-t-transparent" />
      <span className="text-xs text-gray-400">Opening...</span>
    </div>
  ) : (
    <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gray-50 px-4 text-center">
      <HugeiconsIcon icon={Folder01Icon} size={20} strokeWidth={2} color="currentColor" className="w-5 h-5 text-gray-400" aria-hidden="true" />
      <span className="text-xs font-medium text-gray-600">Install the extension to open this site</span>
      <span className="text-xs text-gray-400">Opens automatically once installed</span>
    </div>
  );
}

export function RecentProjectsSkeleton() {
  return (
    <section aria-label="Recent projects loading" className="flex flex-col">
      <div className="section-label mb-3 flex items-center gap-2">
        <div className="w-4 h-4 rounded skeleton-shimmer" />
        <div className="h-3 w-20 rounded skeleton-shimmer" />
      </div>
      <div className="flex-1">
        <div className={GRID}>
          {Array.from({ length: RECENT_PROJECTS_LIMIT }, (_, i) => (
            <ProjectCardSkeleton key={i} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
